import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import Alert from 'react-s-alert';
import PageHeader from '../pageHeader/PageHeader.jsx';
import { json as ajax } from '../../ajax.js';

// The usage endpoint parses dates as YYYY-MM-DD; keep this independent of the
// MM/DD/YYYY display format so the request always matches what the API expects.
const API_DATE_FORMAT = 'YYYY-MM-DD';
const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

// Bucket granularities the API supports (its date_trunc units).
const WINDOWS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

// Friendly labels for the endpoint names the API records.
const ENDPOINT_LABELS = {
  token: 'Token',
  vin_lookup: 'VIN Lookup',
  component_search: 'Component Search',
  component_details: 'Component Details',
};

// formatPeriod renders a bucket's period_start according to the grouping window.
function formatPeriod (iso, window) {
  const m = moment(iso);
  if (window === 'year') { return m.format('YYYY'); }
  if (window === 'month') { return m.format('MMM YYYY'); }
  if (window === 'week') { return 'Week of ' + m.format(DISPLAY_DATE_FORMAT); }
  return m.format(DISPLAY_DATE_FORMAT);
}

export default function AllDataUsageReport (props) {
  // Default view: the last 12 months grouped by month — the headline the report
  // exists for. Presets and the Group by control switch granularity from here.
  const [beginDate, setBeginDate] = useState(moment().subtract(1, 'year'));
  const [endDate, setEndDate] = useState(moment());
  const [window, setWindow] = useState('month');
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadedRange, setLoadedRange] = useState(null);
  const [loadedWindow, setLoadedWindow] = useState('month');

  useEffect(() => {
    document.title = 'TCA Portal - AllData API Usage';
    fetchUsage(moment().subtract(1, 'year'), moment(), 'month');
  }, []);

  function fetchUsage (from, to, win) {
    if (!from || !from.isValid() || !to || !to.isValid()) {
      Alert.error('Please choose a valid start and end date.');
      return;
    }
    if (to.isBefore(from, 'day')) {
      Alert.error('End date cannot be before start date.');
      return;
    }

    setLoading(true);
    // The API's `to` bound is exclusive, so add a day so the selected end date
    // is included in full.
    const url = '/api/all-data/usage?window=' + win
      + '&from=' + from.format(API_DATE_FORMAT)
      + '&to=' + to.clone().add(1, 'days').format(API_DATE_FORMAT);

    ajax(url, {}, {}, (data, status) => {
      setLoading(false);
      if (status === 200) {
        setUsage(data.usage || []);
        setLoadedRange({ from: from.clone(), to: to.clone() });
        setLoadedWindow(win);
      } else {
        Alert.error((data && data.message) || 'Could not load AllData API usage. Please try again.');
      }
    });
  }

  function handleApply () {
    fetchUsage(beginDate, endDate, window);
  }

  // Quick ranges pair a span with a sensible default grouping.
  function applyPreset (from, to, win) {
    setBeginDate(from);
    setEndDate(to);
    setWindow(win);
    fetchUsage(from, to, win);
  }

  // The API returns (period, endpoint) buckets. Roll them up two ways: by
  // endpoint (which endpoints cost the most) and by period (usage over time).
  const perEndpoint = {};
  const perPeriod = {};
  const totals = { live_count: 0, cache_hits: 0, error_count: 0, rate_limited_count: 0, total: 0 };
  usage.forEach((bucket) => {
    const e = perEndpoint[bucket.endpoint] ||
      { endpoint: bucket.endpoint, live_count: 0, cache_hits: 0, error_count: 0, rate_limited_count: 0, total: 0 };
    const p = perPeriod[bucket.period_start] ||
      { period_start: bucket.period_start, live_count: 0, cache_hits: 0, error_count: 0, rate_limited_count: 0, total: 0 };
    ['live_count', 'cache_hits', 'error_count', 'rate_limited_count', 'total'].forEach((f) => {
      e[f] += bucket[f] || 0;
      p[f] += bucket[f] || 0;
      totals[f] += bucket[f] || 0;
    });
    perEndpoint[bucket.endpoint] = e;
    perPeriod[bucket.period_start] = p;
  });
  const endpointRows = Object.keys(perEndpoint).sort().map((key) => perEndpoint[key]);
  const periodRows = Object.keys(perPeriod).sort().map((key) => perPeriod[key]);
  const cacheHitRate = totals.total > 0 ? Math.round((totals.cache_hits / totals.total) * 100) : 0;

  return (
    <div>
      <section className="gap-dashboard clearfix">
        <div className="row">
          <div className="col-12 mt-5">
            <PageHeader pageTitle="AllData API Usage" user={props.user} />

            <div className="row align-items-end">
              <div className="col-3">
                <label style={{ marginLeft: 12, marginBottom: 5 }}>Start Date</label>
                <DatePicker className="form-control"
                  id="alldata-usage-begin-date"
                  selected={beginDate}
                  onChange={setBeginDate}
                />
              </div>
              <div className="col-3">
                <label style={{ marginLeft: 12, marginBottom: 5 }}>End Date</label>
                <DatePicker className="form-control"
                  id="alldata-usage-end-date"
                  selected={endDate}
                  onChange={setEndDate}
                />
              </div>
              <div className="col-2">
                <label style={{ marginLeft: 12, marginBottom: 5 }}>Group by</label>
                <select className="form-control"
                  id="alldata-usage-window"
                  value={window}
                  onChange={(e) => setWindow(e.target.value)}>
                  {WINDOWS.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-2">
                <button className="btn btn-primary"
                  id="alldata-usage-apply"
                  style={{ marginTop: 28 }}
                  type="button"
                  onClick={handleApply}
                  disabled={loading}>
                  {loading ? 'Loading...' : 'Apply'}
                </button>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-12" style={{ marginLeft: 12 }}>
                <span className="text-muted mr-2">Quick range:</span>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-2"
                  id="alldata-usage-preset-7d"
                  onClick={() => applyPreset(moment().subtract(6, 'days'), moment(), 'day')}>
                  Last 7 days
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-2"
                  id="alldata-usage-preset-30d"
                  onClick={() => applyPreset(moment().subtract(29, 'days'), moment(), 'day')}>
                  Last 30 days
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-2"
                  id="alldata-usage-preset-12m"
                  onClick={() => applyPreset(moment().subtract(1, 'year'), moment(), 'month')}>
                  Last 12 months
                </button>
              </div>
            </div>

            {loadedRange &&
              <p className="text-muted mt-2" style={{ marginLeft: 12 }}>
                Showing usage from {loadedRange.from.format(DISPLAY_DATE_FORMAT)}
                {' to '}
                {loadedRange.to.format(DISPLAY_DATE_FORMAT)}
                {' grouped by '}
                {loadedWindow}
              </p>
            }

            <div className="row mt-3">
              <div className="col-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Total API Calls</h6>
                    <h2 id="alldata-usage-total-calls">{totals.total}</h2>
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Cache Hits</h6>
                    <h2 id="alldata-usage-total-cache-hits">{totals.cache_hits}</h2>
                    <small className="text-muted">{cacheHitRate}% of calls served from cache</small>
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Live Upstream Calls</h6>
                    <h2 id="alldata-usage-total-live">{totals.live_count}</h2>
                  </div>
                </div>
              </div>
              <div className="col-3">
                <div className="card text-center">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Upstream Errors</h6>
                    <h2 id="alldata-usage-total-errors">{totals.error_count}</h2>
                    <small className="text-muted">{totals.rate_limited_count} rate-limited (429)</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <h5 style={{ marginLeft: 12 }}>By Endpoint</h5>
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Endpoint</th>
                      <th className="text-right">Live Calls</th>
                      <th className="text-right">Cache Hits</th>
                      <th className="text-right">Errors</th>
                      <th className="text-right">Rate-limited</th>
                      <th className="text-right">Total Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {endpointRows.length === 0 &&
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No API usage in the selected period.
                        </td>
                      </tr>
                    }
                    {endpointRows.map((row) => (
                      <tr key={row.endpoint}>
                        <td>{ENDPOINT_LABELS[row.endpoint] || row.endpoint}</td>
                        <td className="text-right">{row.live_count}</td>
                        <td className="text-right">{row.cache_hits}</td>
                        <td className="text-right">{row.error_count}</td>
                        <td className="text-right">{row.rate_limited_count}</td>
                        <td className="text-right">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                  {endpointRows.length > 0 &&
                    <tfoot>
                      <tr>
                        <th>Total</th>
                        <th className="text-right">{totals.live_count}</th>
                        <th className="text-right">{totals.cache_hits}</th>
                        <th className="text-right">{totals.error_count}</th>
                        <th className="text-right">{totals.rate_limited_count}</th>
                        <th className="text-right">{totals.total}</th>
                      </tr>
                    </tfoot>
                  }
                </table>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <h5 style={{ marginLeft: 12 }}>Over Time</h5>
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th className="text-right">Live Calls</th>
                      <th className="text-right">Cache Hits</th>
                      <th className="text-right">Errors</th>
                      <th className="text-right">Rate-limited</th>
                      <th className="text-right">Total Calls</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periodRows.length === 0 &&
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No API usage in the selected period.
                        </td>
                      </tr>
                    }
                    {periodRows.map((row) => (
                      <tr key={row.period_start}>
                        <td>{formatPeriod(row.period_start, loadedWindow)}</td>
                        <td className="text-right">{row.live_count}</td>
                        <td className="text-right">{row.cache_hits}</td>
                        <td className="text-right">{row.error_count}</td>
                        <td className="text-right">{row.rate_limited_count}</td>
                        <td className="text-right">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

AllDataUsageReport.propTypes = {
  user: PropTypes.object,
};
